import React, { useContext } from "react";

import { TypeToledo } from "@at/design-system-foundation";
import { dataAttrGUI } from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";
import { useAppContext } from "@at/sauron-platform-core";

import * as Styled from "./leasing-pricing.styled";
import { setLeasingModal } from "../../../../../contexts/leasing/actions/leasing.actions";
import {
  LeasingDispatchContext,
  useLeasingStateContext,
} from "../../../../../contexts/leasing/context/leasing.context";
import {
  ELeasingBusinessType,
  LeasingModals,
} from "../../../../../contexts/leasing/types/leasing.types";

import { AbTestNames } from "../../../../../utils/ab-test/ab-test.util";
import { useTracking } from "../../../../../utils/tracking/use-tracking";
import DotsLoader from "../../../../dots-loader/dots-loader";
import { useLeasingData } from "../../hooks/useLeasingData/use-leasing-data.hook";
import { LeasingOrderButton } from "../order-button/leasing-order-button";

export const leasingTermPrice = "leasing-term-price";
export const leasingTermPriceBreakdown = "leasing-term-price-breakdown";

interface LeasingPricingProps {
  leaseOffersLoading: boolean;
}

export const LeasingPricing = ({ leaseOffersLoading }: LeasingPricingProps) => {
  const { selectedBusinessType, leaseOffersData } = useLeasingStateContext();
  const dispatch = useContext(LeasingDispatchContext);
  const { trackEvent } = useTracking();
  const { leaseTermParts, monthlyPrice, deposit, isBusinessContract } =
    useLeasingData();
  const { matchesTestGroups } = useAppContext();
  const leaseOffersAvailable = !!leaseOffersData?.length;

  const isUserInLimitedCheckoutTest = matchesTestGroups(
    AbTestNames.LEASING_LIMITED_CHECKOUT,
  );

  const { mileage, numberOfMonths } = { ...leaseTermParts };

  const isLimitedBusinessContractType =
    selectedBusinessType === ELeasingBusinessType.LIMITED_COMPANY ||
    selectedBusinessType === ELeasingBusinessType.LIMITED_LIABILITY_PARTNERSHIP;

  const contractTypeIntroText = isBusinessContract
    ? "Business lease exc. VAT"
    : "Personal lease inc. VAT";

  const handleContactUsClick = () => {
    dispatch(
      setLeasingModal({
        title: "Contact us",
        show: true,
        type: LeasingModals.CONTACT_US,
      }),
    );
    trackEvent({ trackingLabel: "contact us" });
  };

  const price = leaseOffersLoading ? (
    <DotsLoader />
  ) : leaseOffersAvailable ? (
    <Styled.Price as="span" weight="400">
      {monthlyPrice}{" "}
    </Styled.Price>
  ) : (
    <TypeToledo as="span" weight="400">
      Price unavailable
    </TypeToledo>
  );

  const priceTimescale = leaseOffersAvailable ? (
    <Styled.PriceTimescale as="span">per month</Styled.PriceTimescale>
  ) : null;

  const priceBreakdown = leaseOffersLoading ? (
    <DotsLoader />
  ) : (
    <>
      <Styled.PriceBreakdownItem>
        {leaseOffersAvailable ? (
          <>
            <strong>{deposit}</strong> initial payment{" "}
          </>
        ) : (
          <>
            <strong>-</strong> initial payment{" "}
          </>
        )}
      </Styled.PriceBreakdownItem>
      <Styled.PriceBreakdownItem>
        <strong>{numberOfMonths}</strong> month contract{" "}
      </Styled.PriceBreakdownItem>
      <Styled.PriceBreakdownItem>
        <strong>{mileage?.toLocaleString()}</strong> miles per year
      </Styled.PriceBreakdownItem>
    </>
  );

  return (
    <Styled.LeasingProductPriceArea>
      <Styled.LeasingProductPriceWrap>
        <Styled.PriceDetails {...{ [dataAttrGUI]: leasingTermPrice }}>
          <Styled.PriceDetailsIntro as="span">
            {contractTypeIntroText}{" "}
          </Styled.PriceDetailsIntro>
          {price}
          {priceTimescale}
        </Styled.PriceDetails>

        <Styled.PriceBreakdown
          {...{ [dataAttrGUI]: leasingTermPriceBreakdown }}
        >
          {priceBreakdown}
        </Styled.PriceBreakdown>
      </Styled.LeasingProductPriceWrap>

      <Styled.LeasingProductOrderButtons>
        {(isUserInLimitedCheckoutTest || !isLimitedBusinessContractType) && (
          <LeasingOrderButton />
        )}
        <Styled.LeasingContactUsButton
          {...{ attributes: { [dataAttrGUI]: "contact-us-leasing-button" } }}
          onInteraction={handleContactUsClick}
          text="Contact us"
          displayType="secondary"
          fullWidth
        />
      </Styled.LeasingProductOrderButtons>
    </Styled.LeasingProductPriceArea>
  );
};
